// Session + preference storage for the admin app.
//
// Real auth (PLA-86): persists the admin access/refresh JWTs and the admin
// profile. Mirrors apps/frontend/src/auth/storage.ts.
//
// Offline-dev LOGIN fallback: when VITE_ADMIN_MOCK is true a developer without
// a backend can log in; we mark that with a "mock session" flag so the rest of
// the app treats it like a real session (minus a real token).

import type { AdminUser } from "@/types"

const KEYS = {
  access: "platonica_admin_access",
  refresh: "platonica_admin_refresh",
  admin: "platonica_admin",
  // Offline-dev login fallback marker — presence means "logged in" without a
  // real token (VITE_ADMIN_MOCK only).
  mockSession: "platonica_admin_mock_session",
} as const

export const adminAuthStorage = {
  getAccess: () => localStorage.getItem(KEYS.access),
  getRefresh: () => localStorage.getItem(KEYS.refresh),

  getAdmin: (): AdminUser | null => {
    const raw = localStorage.getItem(KEYS.admin)
    if (!raw) return null
    try {
      const parsed = JSON.parse(raw) as AdminUser
      // Drop a stale/incomplete cached admin — e.g. a pre-rename session whose
      // shape predates `institution`/`role`. There's no /me to refetch, so an
      // invalid cache would otherwise crash the shell (reads admin.institution
      // .name). Returning null falls back to the login screen, where a fresh
      // login repopulates the full object.
      if (!parsed?.institution?.name || !parsed.role) return null
      return parsed
    } catch {
      return null
    }
  },
  setAdmin: (admin: AdminUser) => localStorage.setItem(KEYS.admin, JSON.stringify(admin)),

  setTokens: (access: string, refresh: string) => {
    localStorage.setItem(KEYS.access, access)
    localStorage.setItem(KEYS.refresh, refresh)
  },
  setAccess: (access: string) => localStorage.setItem(KEYS.access, access),

  // Offline-dev login fallback (VITE_ADMIN_MOCK only).
  hasMockSession: () => localStorage.getItem(KEYS.mockSession) === "1",
  setMockSession: () => localStorage.setItem(KEYS.mockSession, "1"),

  clear: () => {
    localStorage.removeItem(KEYS.access)
    localStorage.removeItem(KEYS.refresh)
    localStorage.removeItem(KEYS.admin)
    localStorage.removeItem(KEYS.mockSession)
  },
}
